import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import $ from "jquery";

import { SlUser } from "react-icons/sl";
import { PiShoppingCartSimple } from "react-icons/pi";
import { IoMdHeartEmpty } from "react-icons/io";
import {
  MdDashboard,
  MdLogout,
  MdPerson,
  MdShoppingBag,
  MdChevronRight,
  MdShoppingCart,
} from "react-icons/md";
import { RiAdminFill } from "react-icons/ri";
import { Award, ChevronDown, Gem, Home, Image, Info, Menu, Truck, X } from "lucide-react";

import ApiURl from "../controllers/Api";
import logo from "../assets/logo.png";

const DARK_MAROON = "var(--user-maroon, #2B0000)";
const DARK_MAROON_DEEP = "var(--user-maroon-deep, #1A0000)";
const GOLD = "var(--user-gold, #D4AF37)";
const CREAM = "var(--user-cream, #F4ECE1)";
const NAV_BACKGROUND = "var(--user-bg, #E8D9D5)";

const leftLinks = [
  {
    label: "Home",
    path: "/",
    icon: <Home className="h-[15px] w-[15px]" />,
  },
  {
    label: "All Products",
    path: "/all-products",
    icon: <Gem className="h-[15px] w-[15px]" />,
  },
  {
    label: "About Us",
    path: "/about-us",
    icon: <Info className="h-[15px] w-[15px]" />,
  },
];

const rightLinks = [
  {
    label: "Wishlist",
    path: "/wishlist",
    icon: <IoMdHeartEmpty className="h-[17px] w-[17px]" />,
    badge: "wishlist",
  },
];

const mobileNavigation = [
  {
    label: "Home",
    path: "/",
    icon: <Home className="h-5 w-5" />,
  },
  {
    label: "Shop",
    path: "/all-products",
    icon: <Gem className="h-5 w-5" />,
  },
  {
    label: "About Us",
    path: "/about-us",
    icon: <Info className="h-5 w-5" />,
  },
  {
    label: "Events",
    path: "/events",
    icon: <Award className="h-5 w-5" />,
  },
  {
    label: "Our Team",
    path: "/our-team",
    icon: <Image className="h-5 w-5" />,
  },
];

export default function Navbar({ isAdmin = false } = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  const accountButtonRef = useRef(null);
  const accountMenuRef = useRef(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const [loggedIn, setLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState("");

  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const accountMenuItems = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <MdDashboard className="h-5 w-5" />,
    },
    {
      label: "Orders",
      path: "/order-section",
      icon: <MdShoppingBag className="h-5 w-5" />,
    },
    {
      label: "Profile",
      path: "/profile",
      icon: <MdPerson className="h-5 w-5" />,
    },
  ];

  const formatBadgeText = (count) => {
    return count > 99 ? "99+" : String(count);
  };

  const computeCartCountFromLocalStorage = () => {
    try {
      const storedCart = localStorage.getItem("cart");

      if (!storedCart) {
        return 0;
      }

      const cartItems = JSON.parse(storedCart);

      if (!Array.isArray(cartItems)) {
        return 0;
      }

      return cartItems.reduce((total, item) => {
        const quantity = Number(item?.quantity || 1);

        return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
      }, 0);
    } catch (error) {
      console.error("Unable to read cart:", error);
      return 0;
    }
  };

  const fetchWishlistCount = async () => {
    const userId = localStorage.getItem("user_id");

    if (!userId) {
      setWishlistCount(0);
      return;
    }

    try {
      const response = await $.getJSON(
        `${ApiURl}/getWishlistItems.php?user_id=${userId}`
      );

      setWishlistCount(response?.wishlist?.length || 0);
    } catch (error) {
      console.error("Unable to fetch wishlist count:", error);
      setWishlistCount(0);
    }
  };

  const checkAuthStatus = () => {
    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("token");

    if (role && userId && token) {
      setUserRole(role);
      setLoggedIn(true);
      fetchWishlistCount();
    } else {
      setUserRole("");
      setLoggedIn(false);
      setWishlistCount(0);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("role");
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("user_id");
    localStorage.removeItem("phone");
    localStorage.removeItem("cart_id");
    localStorage.removeItem("userData");

    setUserRole("");
    setLoggedIn(false);
    setWishlistCount(0);
    setAccountOpen(false);
    setMobileOpen(false);

    window.dispatchEvent(new Event("authChanged"));

    navigate("/login");
  };

  const handleAdminClick = () => {
    setAccountOpen(false);
    setMobileOpen(false);
    navigate("/admin");
  };

  const isActiveLink = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname.startsWith(path);
  };

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    checkAuthStatus();
    setCartCount(computeCartCountFromLocalStorage());

    const handleAuthChange = () => {
      checkAuthStatus();
    };

    const handleStorageChange = (event) => {
      if (
        event.key === "role" ||
        event.key === "user_id" ||
        event.key === "token"
      ) {
        checkAuthStatus();
      }

      if (event.key === "cart") {
        setCartCount(computeCartCountFromLocalStorage());
      }

      if (event.key === "wishlist_updated") {
        fetchWishlistCount();
      }
    };

    const handleWindowFocus = () => {
      checkAuthStatus();
      setCartCount(computeCartCountFromLocalStorage());
      fetchWishlistCount();
    };

    window.addEventListener("authChanged", handleAuthChange);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleWindowFocus);

    const cartInterval = window.setInterval(() => {
      setCartCount(computeCartCountFromLocalStorage());
    }, 2000);

    const wishlistInterval = window.setInterval(() => {
      fetchWishlistCount();
    }, 4000);

    return () => {
      window.removeEventListener("authChanged", handleAuthChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleWindowFocus);

      window.clearInterval(cartInterval);
      window.clearInterval(wishlistInterval);
    };
  }, []);

  useEffect(() => {
    setAccountOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideMenu =
        accountMenuRef.current?.contains(event.target);

      const clickedAccountButton =
        accountButtonRef.current?.contains(event.target);

      if (
        accountOpen &&
        !clickedInsideMenu &&
        !clickedAccountButton
      ) {
        setAccountOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [accountOpen]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.removeProperty("overflow");
    }

    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [mobileOpen]);

  const CartBadge = () => {
    if (cartCount <= 0) {
      return null;
    }

    return (
      <span
        className="
          absolute -right-2 -top-2
          flex h-[17px] min-w-[17px] items-center justify-center
          rounded-full px-1 text-[9px] font-bold
        "
        style={{
          backgroundColor: GOLD,
          color: DARK_MAROON_DEEP,
          boxShadow: `0 0 0 2px ${DARK_MAROON}`,
        }}
      >
        {formatBadgeText(cartCount)}
      </span>
    );
  };

  const WishlistBadge = () => {
    if (wishlistCount <= 0) {
      return null;
    }

    return (
      <span
        className="
          absolute -right-2 -top-2
          flex h-[17px] min-w-[17px] items-center justify-center
          rounded-full px-1 text-[9px] font-bold
        "
        style={{
          backgroundColor: GOLD,
          color: DARK_MAROON_DEEP,
          boxShadow: `0 0 0 2px ${DARK_MAROON}`,
        }}
      >
        {formatBadgeText(wishlistCount)}
      </span>
    );
  };

  return (
    <>
      <header
        className="absolute inset-x-0 top-0 z-[999] transition-colors duration-300"
        style={{
          backgroundColor: isHomePage ? "transparent" : NAV_BACKGROUND,
        }}
      >
        {/* ===================== DESKTOP NAVBAR ===================== */}

        <div
          className={`
            relative mx-auto hidden max-w-[1050px]
            items-center justify-center
            h-[96px] transition-shadow duration-300 md:flex
          `}
        >
          <div
            className="
              grid w-full grid-cols-[1fr_auto_1fr]
              items-center gap-[44px]
              px-6 lg:gap-[42px]
            "
          >
            {/* LEFT PILL */}

            <div className="flex justify-end">
              <nav
                className="
                  flex items-center rounded-full
                  px-[7px] py-[6px]
                  shadow-[0_14px_32px_rgba(43,0,0,0.2)]
                "
                style={{
                  backgroundColor: DARK_MAROON,
                }}
              >
                {leftLinks.map((item) => {
                  const active = isActiveLink(item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`
                        flex h-[36px] items-center justify-center
                        gap-[7px] rounded-full px-[15px]
                        font-serif text-[14px] font-semibold
                        whitespace-nowrap
                        transition-all duration-300
                        ${
                          active
                            ? "bg-white/10 text-[#E9CD7A]"
                            : "text-white hover:bg-white/10 hover:text-[#E9CD7A]"
                        }
                      `}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* CENTER LOGO */}

            <Link
              to="/"
              aria-label="Go to homepage"
              className="
                flex items-center justify-center
                transition-transform duration-300
                hover:scale-[1.02]
              "
            >
              <img
                src={logo}
                alt="SL Gold"
                className={`
                  w-auto object-contain
                  h-[99px] transition-transform duration-300
                `}
              />
            </Link>

            {/* RIGHT PILL */}

            <div className="flex justify-start">
              <div
                className="
                  flex items-center rounded-full
                  px-[7px] py-[6px]
                  shadow-[0_14px_32px_rgba(43,0,0,0.2)]
                "
                style={{
                  backgroundColor: DARK_MAROON,
                }}
              >
                {rightLinks.map((item) => {
                  const active = isActiveLink(item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`
                        flex h-[36px] items-center justify-center
                        gap-[7px] rounded-full px-[15px]
                        font-serif text-[14px] font-semibold
                        whitespace-nowrap
                        transition-all duration-300
                        ${
                          active
                            ? "bg-white/10 text-[#E9CD7A]"
                            : "text-white hover:bg-white/10 hover:text-[#E9CD7A]"
                        }
                      `}
                    >
                      <span className="relative">
                        {item.icon}
                        {item.badge === "wishlist" && <WishlistBadge />}
                      </span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}

                <Link
                  to="/cart"
                  className={`
                    flex h-[36px] items-center justify-center
                    gap-[7px] rounded-full px-[16px]
                    font-serif text-[14px] font-semibold
                    whitespace-nowrap
                    transition-all duration-300
                    ${
                      isActiveLink("/cart")
                        ? "bg-white/10 text-[#E9CD7A]"
                        : "text-white hover:bg-white/10 hover:text-[#E9CD7A]"
                    }
                  `}
                >
                  <span className="relative">
                    <PiShoppingCartSimple className="h-[16px] w-[16px]" />
                    <CartBadge />
                  </span>

                  <span>My Cart</span>
                </Link>

                <div className="relative">
                  <button
                    ref={accountButtonRef}
                    type="button"
                    onClick={() => setAccountOpen((current) => !current)}
                    className={`
                      flex h-[36px] items-center justify-center
                      gap-[7px] rounded-full px-[16px]
                      font-serif text-[14px] font-semibold
                      whitespace-nowrap
                      transition-all duration-300
                      ${
                        accountOpen ||
                        isActiveLink("/profile") ||
                        isActiveLink("/dashboard")
                          ? "bg-white/10 text-[#E9CD7A]"
                          : "text-white hover:bg-white/10 hover:text-[#E9CD7A]"
                      }
                    `}
                    aria-label="Open profile menu"
                    aria-expanded={accountOpen}
                  >
                    <SlUser className="h-[15px] w-[15px]" />

                    <span>Profile</span>

                    <ChevronDown
                      className={`
                        h-3 w-3 transition-transform duration-300
                        ${accountOpen ? "rotate-180" : ""}
                      `}
                    />
                  </button>

                  {accountOpen && (
                    <div
                      ref={accountMenuRef}
                      className="
                        absolute right-0 top-[calc(100%+14px)]
                        w-[235px] overflow-hidden rounded-[20px]
                        border border-white/70 bg-[#FFFDF9]
                        p-2.5
                        shadow-[0_26px_70px_rgba(43,0,0,0.22)]
                      "
                    >
                      <div
                        className="mb-2 rounded-[15px] px-4 py-3"
                        style={{
                          backgroundColor: "var(--user-cream, #F5ECE7)",
                        }}
                      >
                        <p
                          className="
                            text-[10px] font-semibold uppercase
                            tracking-[0.22em]
                          "
                          style={{
                            color: DARK_MAROON,
                          }}
                        >
                          My Account
                        </p>

                        <p className="mt-1 text-xs text-[#756A64]">
                          {loggedIn
                            ? "Manage your account"
                            : "Login to access your account"}
                        </p>
                      </div>

                      {loggedIn ? (
                        <>
                          {accountMenuItems.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setAccountOpen(false)}
                              className="
                                flex items-center gap-3 rounded-[13px]
                                px-4 py-3 text-sm font-medium text-[#302722]
                                transition-colors duration-200
                                hover:bg-[#F5ECE7]
                              "
                            >
                              <span style={{ color: DARK_MAROON }}>
                                {item.icon}
                              </span>

                              <span>{item.label}</span>
                            </Link>
                          ))}

                          <Link
                            to="/wishlist"
                            onClick={() => setAccountOpen(false)}
                            className="
                              flex items-center gap-3 rounded-[13px]
                              px-4 py-3 text-sm font-medium text-[#302722]
                              transition-colors duration-200
                              hover:bg-[#F5ECE7]
                            "
                          >
                            <span
                              className="relative"
                              style={{ color: DARK_MAROON }}
                            >
                              <IoMdHeartEmpty className="h-5 w-5" />
                              <WishlistBadge />
                            </span>

                            <span>Wishlist</span>
                          </Link>

                          {userRole === "admin" && (
                            <button
                              type="button"
                              onClick={handleAdminClick}
                              className="
                                flex w-full items-center gap-3
                                rounded-[13px] px-4 py-3
                                text-left text-sm font-medium
                                text-purple-700
                                transition-colors duration-200
                                hover:bg-purple-50
                              "
                            >
                              <RiAdminFill className="h-5 w-5" />
                              <span>Admin Panel</span>
                            </button>
                          )}

                          <div className="my-2 h-px bg-[#E9DDD7]" />

                          <button
                            type="button"
                            onClick={handleLogout}
                            className="
                              flex w-full items-center gap-3
                              rounded-[13px] px-4 py-3
                              text-left text-sm font-semibold text-red-600
                              transition-colors duration-200
                              hover:bg-red-50
                            "
                          >
                            <MdLogout className="h-5 w-5" />
                            <span>Logout</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            onClick={() => setAccountOpen(false)}
                            className="
                              flex items-center gap-3 rounded-[13px]
                              px-4 py-3 text-sm font-semibold text-[#302722]
                              transition-colors duration-200
                              hover:bg-[#F5ECE7]
                            "
                          >
                            <SlUser
                              className="h-5 w-5"
                              style={{ color: DARK_MAROON }}
                            />
                            <span>Login</span>
                          </Link>

                          <Link
                            to="/login"
                            onClick={() => setAccountOpen(false)}
                            className="
                              flex items-center gap-3 rounded-[13px]
                              px-4 py-3 text-sm font-semibold text-[#302722]
                              transition-colors duration-200
                              hover:bg-[#F5ECE7]
                            "
                          >
                            <MdPerson
                              className="h-5 w-5"
                              style={{ color: DARK_MAROON }}
                            />
                            <span>Create Account</span>
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== MOBILE NAVBAR ===================== */}

        <div
          className="relative flex h-[72px] items-center justify-between px-4 transition-shadow duration-300 md:hidden"
          style={{
            backgroundColor: "transparent",
            backdropFilter: "none",
          }}
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="grid h-10 w-10 place-items-center text-[#2B0000] transition-transform duration-200 active:scale-95"
            aria-label="Open navigation"
          >
            <Menu className="h-[22px] w-[22px]" strokeWidth={1.7} />
          </button>

          <Link
            to="/"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            aria-label="Go to homepage"
          >
            <img
              src={logo}
              alt="SL Gold"
              className="h-[62px] w-auto object-contain"
            />
          </Link>

          <div className="flex items-center gap-1">
            <Link
              to="/cart"
              className="relative grid h-10 w-10 place-items-center text-[#2B0000]"
              aria-label="Open cart"
            >
              <PiShoppingCartSimple className="h-[22px] w-[22px]" />
              <CartBadge />
            </Link>
          </div>
        </div>
      </header>

      {/* ===================== MOBILE DRAWER ===================== */}

      <div
        className={`fixed inset-0 z-[1000] md:hidden ${
          mobileOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className={`absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-300 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          aria-label="Close navigation overlay"
        />

        <aside
          className={`absolute inset-y-0 left-0 w-[78%] max-w-[330px] overflow-y-auto shadow-[24px_0_80px_rgba(0,0,0,0.38)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{
            background:
              "radial-gradient(circle at 38% 6%, rgba(107,20,16,0.48), transparent 26%), linear-gradient(180deg, #270000 0%, #180000 48%, #120000 100%)",
          }}
        >
          <div className="flex min-h-full flex-col px-4 pb-5 pt-4 text-white">
            <div className="relative flex min-h-[102px] items-start justify-center">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex flex-col items-center"
              >
                <img
                  src={logo}
                  alt="SL Gold"
                  className="h-[84px] w-auto object-contain brightness-0 invert-[76%] sepia-[70%] saturate-[500%] hue-rotate-[355deg]"
                />
              </Link>

              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="absolute right-0 top-1 grid h-9 w-9 place-items-center text-white/70 transition-colors hover:text-white"
                aria-label="Close navigation"
              >
                <X className="h-6 w-6" strokeWidth={1.6} />
              </button>
            </div>

            <nav className="mt-2">
              {mobileNavigation.map((item) => {
                const active = isActiveLink(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`group flex min-h-[48px] items-center gap-3 border-b border-white/[0.035] px-1 text-[14px] font-medium transition-colors duration-200 ${
                      active ? "text-[#E9CD7A]" : "text-white/88 hover:text-[#E9CD7A]"
                    }`}
                  >
                    <span
                      className="grid h-7 w-7 shrink-0 place-items-center"
                      style={{ color: GOLD }}
                    >
                      {item.icon || <Gem className="h-5 w-5" />}
                    </span>

                    <span>{item.label}</span>

                    {item.label !== "Home" && (
                      <MdChevronRight
                        className="ml-auto h-[18px] w-[18px] text-[#D4AF37]/70 transition-transform duration-200 group-hover:translate-x-0.5"
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="my-3 h-px bg-white/10" />

            <div>
              {loggedIn ? (
                <>
                  {accountMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="group flex min-h-[46px] items-center gap-3 px-1 text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                    >
                      <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                      <MdChevronRight className="ml-auto h-[18px] w-[18px] text-[#D4AF37]/65 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                  ))}

                  <Link
                    to="/wishlist"
                    onClick={() => setMobileOpen(false)}
                    className="group flex min-h-[46px] items-center gap-3 px-1 text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                  >
                    <span className="relative grid h-7 w-7 place-items-center text-[#D4AF37]">
                      <IoMdHeartEmpty className="h-5 w-5" />
                    </span>
                    <span>Wishlist</span>
                    {wishlistCount > 0 && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C5242B] px-1 text-[10px] font-bold text-white">
                        {formatBadgeText(wishlistCount)}
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/cart"
                    onClick={() => setMobileOpen(false)}
                    className="group flex min-h-[46px] items-center gap-3 px-1 text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                  >
                    <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                      <MdShoppingCart className="h-5 w-5" />
                    </span>
                    <span>My Cart</span>
                    {cartCount > 0 && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C5242B] px-1 text-[10px] font-bold text-white">
                        {formatBadgeText(cartCount)}
                      </span>
                    )}
                  </Link>

                  {userRole === "admin" && (
                    <button
                      type="button"
                      onClick={handleAdminClick}
                      className="group flex min-h-[46px] w-full items-center gap-3 px-1 text-left text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                    >
                      <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                        <RiAdminFill className="h-5 w-5" />
                      </span>
                      <span>Admin Panel</span>
                      <MdChevronRight className="ml-auto h-[18px] w-[18px] text-[#D4AF37]/65" />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="group flex min-h-[46px] w-full items-center gap-3 px-1 text-left text-[14px] font-medium text-white/70 transition-colors duration-200 hover:text-red-300"
                  >
                    <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                      <MdLogout className="h-5 w-5" />
                    </span>
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="group flex min-h-[46px] items-center gap-3 px-1 text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                  >
                    <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                      <SlUser className="h-5 w-5" />
                    </span>
                    <span>My Account</span>
                    <MdChevronRight className="ml-auto h-[18px] w-[18px] text-[#D4AF37]/65" />
                  </Link>

                  <Link
                    to="/wishlist"
                    onClick={() => setMobileOpen(false)}
                    className="group flex min-h-[46px] items-center gap-3 px-1 text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                  >
                    <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                      <IoMdHeartEmpty className="h-5 w-5" />
                    </span>
                    <span>Wishlist</span>
                    {wishlistCount > 0 && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C5242B] px-1 text-[10px] font-bold text-white">
                        {formatBadgeText(wishlistCount)}
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/cart"
                    onClick={() => setMobileOpen(false)}
                    className="group flex min-h-[46px] items-center gap-3 px-1 text-[14px] font-medium text-white/88 transition-colors duration-200 hover:text-[#E9CD7A]"
                  >
                    <span className="grid h-7 w-7 place-items-center text-[#D4AF37]">
                      <MdShoppingCart className="h-5 w-5" />
                    </span>
                    <span>My Cart</span>
                    {cartCount > 0 && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C5242B] px-1 text-[10px] font-bold text-white">
                        {formatBadgeText(cartCount)}
                      </span>
                    )}
                  </Link>
                </>
              )}
            </div>

            <div className="mt-auto pt-5">
              <div className="rounded-[7px] border border-[#D4AF37]/25 bg-[#4A0808]/55 px-3.5 py-3 shadow-[0_14px_35px_rgba(0,0,0,0.16)]">
                <div className="flex items-center gap-3">
                  <span className="grid h-8 w-8 place-items-center text-[#E9CD7A]">
                    <Truck className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[12px] font-semibold text-white">Free Shipping</p>
                    <p className="mt-0.5 text-[9px] text-white/60">
                      On all orders above ₹20,000
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 h-px bg-white/10" />
            </div>
          </div>
        </aside>
      </div>

      {!isAdmin && !isHomePage && (
        <>
          <div className="h-[58px] md:hidden" />
          <div className="hidden h-[74px] md:block" />
        </>
      )}
    </>
  );
}